// ─────────────────────────────────────────────────────────────────────────────
// Restablecimiento de contraseña Frontline (/app/restablecer?token=). El colaborador llega desde el
// enlace del correo y captura su contraseña nueva. Consume POST v1/users/reset-password.
//
// Se calca de `FrontlineActivateView`, que resuelve exactamente el mismo problema (token en la URL +
// captura de contraseña + estados de error), con dos diferencias deliberadas:
//   · No pide aceptaciones legales: quien restablece YA las aceptó al registrarse.
//   · Los errores van en `AuthErrorModal` y no en un `<p class="rp-error">` bajo el formulario, que
//     es la convención que el login ya tomó — un texto pequeño al fondo se confunde con el resto de
//     leyendas y pasa desapercibido.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useResetPassword } from '@/api/recognition';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { UserProvider } from '@/contexts/UserContext';
import { LanguageSelector, AuthLogo, AuthField, AuthHelp, AuthErrorModal } from './accessShared';
import { esErrorTemporal } from './accessErrors';
import '../recognition.css';
import { withBase } from '@/utils/basePath';

/** La misma que aplica el servidor (FrontlinePasswordPolicy). Aquí sólo evita un viaje inútil. */
const MIN_PASSWORD = 8;

function ResetForm({ token }: Readonly<{ token: string }>) {
  const { t } = useTranslation('recognition');
  const reset = useResetPassword();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mensajeDelServidor = (fallo: unknown): string => {
    if (esErrorTemporal(fallo)) {
      return t('reset.errTemporary', 'No pudimos procesar tu solicitud en este momento. Revisa tu conexión y vuelve a intentarlo; tu enlace sigue siendo válido.');
    }
    return t('reset.errInvalidLink', 'El enlace no es válido o ya expiró. Solicita uno nuevo desde el inicio de sesión.');
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (password.length < MIN_PASSWORD) {
      setError(t('reset.errPassword', 'La contraseña debe tener al menos 8 caracteres.'));
      return;
    }
    if (password !== confirm) {
      setError(t('reset.errPasswordMatch', 'Las contraseñas no coinciden.'));
      return;
    }
    setError(null);
    reset.mutate({ token, password });
  };

  if (reset.isSuccess) {
    return (
      <div className="acc-success" role="status">
        <div className="acc-success-icon">✅</div>
        <h2>{t('reset.doneTitle', 'Contraseña actualizada')}</h2>
        <p>{t('reset.doneSub', 'Ya puedes iniciar sesión con tu correo y tu contraseña nueva.')}</p>
        <a className="acc-submit acc-submit--link" href={withBase('/login')}>
          {t('login.submit', 'Iniciar sesión')} <i className="fas fa-arrow-right" />
        </a>
      </div>
    );
  }

  return (
    <form className="acc-form" onSubmit={submit} noValidate>
      <AuthField
        icon="fa-lock" withEye value={password} onChange={setPassword}
        placeholder={t('reset.password', 'Contraseña nueva (mín. 8 caracteres)')}
        autoComplete="new-password"
      />
      <AuthField
        icon="fa-lock" withEye value={confirm} onChange={setConfirm}
        placeholder={t('reset.confirmPassword', 'Confirmar contraseña')}
        autoComplete="new-password"
      />

      <button type="submit" className="acc-submit" disabled={reset.isPending}>
        {reset.isPending ? t('admin.saving', 'Guardando…') : t('reset.submit', 'Cambiar mi contraseña')}
        {' '}<i className="fas fa-arrow-right" />
      </button>

      <AuthHelp />

      {/* El backend contesta un 400 GENÉRICO: caducado, ya usado o inexistente son el mismo error a
          propósito, para que la respuesta no confirme que ese enlace existió. Así que la pantalla
          tampoco los distingue — y ofrece la salida, que es pedir uno nuevo. */}
      {/* 🔴 Sólo un 4xx dice «enlace no válido». Un fallo de red o del servidor (5xx, 429) no tiene nada
          que ver con el enlace, y decirle «ya expiró» a quien sólo perdió la conexión lo manda a pedir otro
          enlace cuando le bastaba reintentar. */}
      <AuthErrorModal
        message={error ?? (reset.isError ? mensajeDelServidor(reset.error) : null)}
        onClose={() => { setError(null); reset.reset(); }}
      />
    </form>
  );
}

function ResetContent() {
  const { t } = useTranslation('recognition');
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token'), [params]);
  // Llegó desde el login con una contraseña TEMPORAL: el texto lo dice. Se lee en el primer render, antes de que el
  // efecto de abajo limpie la barra de direcciones.
  const temporal = useMemo(() => params.get('origen') === 'temporal', [params]);

  // 🔴 El token sale de la barra de direcciones en cuanto se lee. Con él ahí, viaja en el
  // `Referer` de cualquier recurso externo que cargue la página y queda en el historial del
  // navegador, que en un equipo de planta es compartido. Es la llave de la cuenta durante 60
  // minutos: el estado del router lo conserva para el envío, la URL ya no.
  useEffect(() => {
    if (!token) return;
    window.history.replaceState({}, '', window.location.pathname);
  }, [token]);

  return (
    <div className="acc-page">
      <div className="acc-topbar"><LanguageSelector /></div>
      <div className="acc-card">
        <AuthLogo />
        <h1 className="acc-title">
          {temporal ? t('reset.titleTemporary', 'Elige tu contraseña definitiva') : t('reset.title', 'Crea tu contraseña nueva')}
        </h1>
        <p className="acc-subtitle">
          {temporal
            ? t('reset.subtitleTemporary', 'Entraste con una contraseña temporal. Elige una nueva para continuar.')
            : t('reset.subtitle', 'Elige una contraseña para volver a entrar.')}
        </p>

        {token
          ? <ResetForm token={token} />
          : (
            <div className="acc-success" role="alert">
              <div className="acc-success-icon">⚠️</div>
              <h2>{t('reset.noToken', 'Enlace inválido')}</h2>
              <p>{t('reset.noTokenSub', 'El enlace no es válido o está incompleto. Revisa el correo que recibiste.')}</p>
            </div>
          )}

        {/* Siempre una salida a login: sin ella, un enlace caducado es un callejón. */}
        <p className="acc-alt"><a href={withBase('/login')}>{t('register.backToLogin', '← Volver al inicio de sesión')}</a></p>
      </div>
    </div>
  );
}

export function FrontlineResetPasswordView() {
  return (
    <UserProvider>
      <ThemeProvider>
        <main className="acc-standalone">
          <ResetContent />
        </main>
      </ThemeProvider>
    </UserProvider>
  );
}

export default FrontlineResetPasswordView;
